import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DetailDisplay,
  Dialog,
  ErrorMessage,
  Heading1,
  PrimaryButton,
  SingleSelect,
  Text,
  Textfield,
} from "widgets";
import { Tabs, Tab } from "@mui/material";
import {
  useFetchCustomerInfo,
  useFetchPendingOrders,
} from "store/backend/assets";
import Loader from "Shared/Loader/Loader";
import { reduceToFixedDecimals } from "utils/amountCalculation";
import { BUSINESS_VERTICALS_IDS, SEARCH_USER_TYPE } from "constants/config";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import MultipleUsers from "components/MultipleUsers/MultipleUsers";

import styles from "./HomePage.module.scss";

const isUamIdOrPhoneNumberValid = (phoneNumber, uamId, searchType) => {
  if (searchType === SEARCH_USER_TYPE.PHONE_NUMBER) {
    const digits = phoneNumber.replace(/\D/g, "");
    return digits.length === 10;
  }
  return uamId.trim() !== "";
};

const isViewCustomerDetailsButtonDisabled = (
  phoneNumber,
  uamId,
  searchType,
  selectedBusinessVertical
) => {
  return (
    !isUamIdOrPhoneNumberValid(phoneNumber, uamId, searchType) ||
    !selectedBusinessVertical
  );
};

const handlePhoneNumberChange = (event, setPhoneNumber) => {
  const rawValue = event.target.value || "";
  const digitsOnly = rawValue.replace(/\D/g, "");
  setPhoneNumber(digitsOnly.slice(0, 10));
};

const handleOnClickPendingOrder = (
  uamId,
  navigate,
  selectedBusinessVerticalId
) => {
  navigate(
    `/customerOrders?uamId=${uamId}&verticalId=${selectedBusinessVerticalId}`
  );
};

const handleClickViewCustomerDetailsButton = (
  searchType,
  phoneNumber,
  uamId,
  navigate,
  salesPersonPhoneNumber,
  openSnackbar,
  fetchCustomerInfo,
  selectedBusinessVertical,
  salesPersonUamId,
  setMultipleUsersList,
  setShowMultipleUsersDialog
) => {
  if (searchType === SEARCH_USER_TYPE.PHONE_NUMBER) {
    const cleanedPhoneNumber = phoneNumber.replace(/^(\d{2})(\d{10})$/, "$2");

    if (cleanedPhoneNumber === salesPersonPhoneNumber) {
      openSnackbar(
        "Seems like you are creating an order with your number. Please change the mobile number and try again.",
        "error"
      );
      return;
    }
  }
  if (searchType === SEARCH_USER_TYPE.UAM_ID && uamId === salesPersonUamId) {
    openSnackbar(
      "Seems like you are creating an order with your customer Id. Please change the customer Id and try again.",
      "error"
    );
    return;
  }

  if (
    !isUamIdOrPhoneNumberValid(phoneNumber, uamId, searchType) ||
    !selectedBusinessVertical
  )
    return;

  const fetchCustomerInfoPayload =
    searchType === SEARCH_USER_TYPE.PHONE_NUMBER
      ? {
          phone_number: phoneNumber,
          selectedBusinessVertical: selectedBusinessVertical,
        }
      : { uam_id: uamId, selectedBusinessVertical: selectedBusinessVertical };

  fetchCustomerInfo(fetchCustomerInfoPayload, {
    onSuccess: (customerDetails) => {
      if (!Array.isArray(customerDetails)) {
        openSnackbar("Unexpected response format.", "error");
        return;
      }
      if (customerDetails.length > 1) {
        setMultipleUsersList(customerDetails);
        setShowMultipleUsersDialog(true);
        return;
      }
      if (customerDetails.length === 1) {
        const selectedUserId = customerDetails[0].uamId;
        const verticalId = customerDetails[0].verticalId;
        navigate(
          `/customerOrders?uamId=${selectedUserId}&verticalId=${verticalId}`
        );
        return;
      }
      openSnackbar("No customer found.", "error");
    },
    onError: (error) => {
      openSnackbar(error.message || "Failed to fetch customer info.", "error");
    },
  });
};

const getPendingAmount = (pendingOrder) => {
  if (!pendingOrder) return null;
  const pendingOrderAmount =
    pendingOrder.inr_conversion_amount -
    pendingOrder.inr_conversion_amount_paid;
  return reduceToFixedDecimals(pendingOrderAmount);
};

const handleChangeBusinessVertical = (event, setSelectedBusinessVertical) => {
  const selectedBusinessVerticalId = event.target.value;
  setSelectedBusinessVertical(selectedBusinessVerticalId);
};

const HomePage = () => {
  const navigate = useNavigate();
  const [salesPersonPhoneNumber] = useState(
    localStorage.getItem("phoneNumber")
  );
  const [salesPersonUamId] = useState(localStorage.getItem("salesPersonUamId"));
  const [phoneNumber, setPhoneNumber] = useState("");
  const [uamId, setUamId] = useState("");
  const [searchType, setSearchType] = useState(SEARCH_USER_TYPE.PHONE_NUMBER);
  const { openSnackbar } = useSnackbarContext();
  const [selectedBusinessVertical, setSelectedBusinessVertical] = useState();
  const [showMultipleUsersDialog, setShowMultipleUsersDialog] = useState(false);
  const [multipleUsersList, setMultipleUsersList] = useState([]);

  const {
    isPending: isPendingOrderLoading,
    data: pendingOrders,
    error: pendingOrdersError,
  } = useFetchPendingOrders(localStorage.getItem("sales_person_id"));

  const {
    mutate: fetchCustomerInfo,
    isPending: isfetchCustomerInformationLoading,
  } = useFetchCustomerInfo();

  if (pendingOrdersError) {
    return <ErrorMessage>Error fetching Pending Orders</ErrorMessage>;
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.searchCustomerContainer}>
        <Tabs
          className={styles.tabsHeader}
          value={searchType}
          onChange={(e, newValue) => setSearchType(newValue)}
        >
          <Tab
            className={styles.tabLabel}
            label="Search by Phone Number"
            value={SEARCH_USER_TYPE.PHONE_NUMBER}
          />
          <Tab
            className={styles.tabLabel}
            label="Search by Customer ID"
            value={SEARCH_USER_TYPE.UAM_ID}
          />
        </Tabs>
        {searchType === SEARCH_USER_TYPE.PHONE_NUMBER ? (
          <div className={styles.inputContainer}>
            <Text>Please enter customer's registered mobile number.</Text>
            <Textfield
              value={phoneNumber}
              handleOnChange={(event) =>
                handlePhoneNumberChange(event, setPhoneNumber)
              }
              label="Enter a Phone number"
            />
          </div>
        ) : (
          <div className={styles.inputContainer}>
            <Text>Please enter Customer ID.</Text>
            <Textfield
              value={uamId}
              handleOnChange={(e) => setUamId(e.target.value)}
              label="Enter a Customer ID"
            />
          </div>
        )}
        <div className={styles.businessVertical}>
          <Text>Please select the business vertical</Text>
          <SingleSelect
            tag="Select business vertical"
            value={selectedBusinessVertical}
            menuItems={BUSINESS_VERTICALS_IDS}
            handleChange={(event) =>
              handleChangeBusinessVertical(event, setSelectedBusinessVertical)
            }
          />
        </div>
        <div className={styles.viewCustomerDetailsButton}>
          <PrimaryButton
            onClick={() =>
              handleClickViewCustomerDetailsButton(
                searchType,
                phoneNumber,
                uamId,
                navigate,
                salesPersonPhoneNumber,
                openSnackbar,
                fetchCustomerInfo,
                selectedBusinessVertical,
                salesPersonUamId,
                setMultipleUsersList,
                setShowMultipleUsersDialog
              )
            }
            disabled={isViewCustomerDetailsButtonDisabled(
              phoneNumber,
              uamId,
              searchType,
              selectedBusinessVertical
            )}
          >
            {isfetchCustomerInformationLoading ? (
              <Loader />
            ) : (
              "View Customer Details"
            )}
          </PrimaryButton>
        </div>
      </div>
      <div className={styles.cardContainer}>
        <Heading1 heading="Pending Orders" />
        <hr className={styles.horizontalLine} />
        <div className={styles.cardList}>
          {isPendingOrderLoading ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : pendingOrders && pendingOrders.length > 0 ? (
            pendingOrders.map((pendingOrder) => (
              <div
                className={styles.card}
                key={pendingOrder.id}
                onClick={() =>
                  handleOnClickPendingOrder(
                    pendingOrder.customer_id,
                    navigate,
                    pendingOrder.business_unit_id
                  )
                }
              >
                <DetailDisplay
                  title="Customer Id"
                  value={pendingOrder.customer_id}
                />
                <DetailDisplay
                  title="Billing Name"
                  value={pendingOrder.recipient_name}
                />
                <DetailDisplay
                  title=" Billing Phone Number"
                  value={pendingOrder.masked_phone_no}
                />
                <DetailDisplay
                  title="Pending Amount"
                  value={getPendingAmount(pendingOrder)}
                />
                <DetailDisplay
                  title="Order Punch Date"
                  value={new Date(
                    pendingOrder.order_created_date
                  ).toLocaleDateString()}
                />
              </div>
            ))
          ) : (
            <span>No Pending Orders</span>
          )}
        </div>
      </div>
      <Dialog
        open={showMultipleUsersDialog}
        handleClose={() => setShowMultipleUsersDialog(false)}
        height="auto"
      >
        <MultipleUsers
          customerDetails={multipleUsersList}
          handleOnSelectUser={(uamId, verticalId) => {
            setShowMultipleUsersDialog(false);
            navigate(`/customerOrders?uamId=${uamId}&verticalId=${verticalId}`);
          }}
        />
      </Dialog>
    </div>
  );
};

export default HomePage;
